import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  listIntegrationAccounts,
  upsertIntegrationAccount,
  deleteIntegrationAccount,
  type IntegrationAccount,
} from '../../services/integration-account-api.js';

@customElement('cp-instagram-settings')
export class CpInstagramSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _integration: IntegrationAccount | null = null;
  @state() private _accessToken = '';
  @state() private _businessAccountId = '';
  @state() private _accountId = '';
  @state() private _loading = true;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    this._load();
  }

  private async _load(): Promise<void> {
    this._loading = true;
    try {
      const integrations = await listIntegrationAccounts();
      this._integration = integrations.find((i) => i.integration_type === 'instagram') ?? null;
      if (this._integration) {
        this._accessToken = this._integration.config?.access_token ?? '';
        this._businessAccountId = this._integration.config?.business_account_id ?? '';
        this._accountId = this._integration.config?.account_id ?? '';
      }
      this._error = '';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private async _handleConnect(): Promise<void> {
    const token = this._accessToken.trim();
    if (!token) return;
    try {
      await upsertIntegrationAccount('instagram', {
        config: {
          access_token: token,
          ...(this._businessAccountId.trim() ? { business_account_id: this._businessAccountId.trim() } : {}),
          ...(this._accountId.trim() ? { account_id: this._accountId.trim() } : {}),
        },
      });
      await this._load();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Verbinden';
    }
  }

  private async _handleDisconnect(): Promise<void> {
    if (!confirm('Instagram Integration trennen?')) return;
    try {
      await deleteIntegrationAccount('instagram');
      this._accessToken = '';
      this._businessAccountId = '';
      this._accountId = '';
      await this._load();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Trennen';
    }
  }

  private _maskToken(token: string): string {
    if (token.length <= 12) return '***';
    return `${token.slice(0, 6)}...${token.slice(-6)}`;
  }

  render() {
    if (this._loading) return html`<div class="cp-integration"><p class="cp-integration__empty">Laden...</p></div>`;

    return html`
      <div class="cp-integration">
        <h3 class="cp-integration__title">Instagram</h3>
        <p class="cp-integration__desc">Meta Access Token und Account IDs fuer Instagram-Zugriff.</p>

        ${this._error ? html`<p style="color: var(--color-danger, #ff3b30); font-size: var(--font-size-sm);">${this._error}</p>` : nothing}

        ${this._integration ? html`
          <div class="cp-integration__status">
            <span class="cp-integration__status-dot cp-integration__status-dot--ok"></span>
            <div class="cp-integration__status-info">
              <div class="cp-integration__status-label">Instagram verbunden</div>
              <div class="cp-integration__status-detail">
                Token: ${this._maskToken(this._integration.config?.access_token ?? '')}
                ${this._integration.config?.business_account_id ? html` · Account: ${this._integration.config.business_account_id}` : nothing}
              </div>
            </div>
            <button class="cp-integration__btn cp-integration__btn--danger" @click=${this._handleDisconnect}>Trennen</button>
          </div>
        ` : html`
          <div class="cp-integration__form">
            <div class="cp-integration__field">
              <label class="cp-integration__label">Meta Access Token</label>
              <input class="cp-integration__input"
                type="password"
                placeholder="EAA..."
                .value=${this._accessToken}
                @input=${(e: Event) => { this._accessToken = (e.target as HTMLInputElement).value; }}
              />
            </div>
            <div class="cp-integration__field">
              <label class="cp-integration__label">Business Account ID</label>
              <input class="cp-integration__input"
                type="text"
                placeholder="z.B. 17841471045533016"
                .value=${this._businessAccountId}
                @input=${(e: Event) => { this._businessAccountId = (e.target as HTMLInputElement).value; }}
              />
            </div>
            <div class="cp-integration__field">
              <label class="cp-integration__label">Account ID</label>
              <input class="cp-integration__input"
                type="text"
                placeholder="z.B. 17841471045533016"
                .value=${this._accountId}
                @input=${(e: Event) => { this._accountId = (e.target as HTMLInputElement).value; }}
              />
            </div>
            <div class="cp-integration__actions">
              <button class="cp-integration__btn cp-integration__btn--primary"
                ?disabled=${!this._accessToken.trim()}
                @click=${this._handleConnect}>Verbinden</button>
            </div>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-instagram-settings': CpInstagramSettings;
  }
}
